import React, { useEffect, useState } from 'react';
import { PackCopyDetailed, SpecDetail } from '../types';
import {
    deletePackCopySpec,
    fetchPackCopyDetails,
    updatePackCopyName,
    updatePackCopyNotes,
    putPackCopySpec,
    postPackCopySpec,
} from '../utils/backendServices';
import { useParams } from 'react-router-dom';
import { NavigateWithinOrg } from '../utils/navigation';
import { PackCopyContext } from '../contexts/PackCopyContext';
import Spinner from '../components/Spinner';
import { useProjectDetailsContext } from '../contexts/ProjectDetailsContext';

interface PackCopyContextProviderProps {
    children: React.ReactNode;
}

const PackCopyContextProvider: React.FC<PackCopyContextProviderProps> = ({
    children,
}) => {
    const { refresh } = useProjectDetailsContext();
    const { packCopyId } = useParams<{ packCopyId: string }>();

    const [packCopyDetailed, setPackCopyDetailed] =
        useState<PackCopyDetailed | null>(null);
    const [loadingState, setLoadingState] = useState<
        'loading' | 'loaded' | 'error'
    >('loaded');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    if (!packCopyId) {
        return <NavigateWithinOrg to="/projects" />;
    }

    const handlePackCopyRequest = async () => {
        try {
            setLoadingState('loading');
            const response = await fetchPackCopyDetails(packCopyId);
            setPackCopyDetailed(response.pack_copy_detailed);
            setLoadingState('loaded');
        } catch (error) {
            console.error(error);
            setLoadingState('error');
            setErrorMessage('Failed to fetch pack copy details');
        }
    };

    useEffect(() => {
        if (packCopyId !== packCopyDetailed?.id) {
            setPackCopyDetailed(null);
        }
        handlePackCopyRequest();
    }, [packCopyId]);

    if (loadingState === 'loading') {
        return <Spinner text="Loading..." />;
    }

    if (loadingState === 'error' || !packCopyDetailed) {
        return (
            <Spinner errorMessage={errorMessage || 'Error loading pack copy'} />
        );
    }

    const state = {
        packCopyDetailed,
        loadingState,
        errorMessage,
    };

    const handlers = {
        handleSpecDelete: async (specName: string) => {
            setLoadingState('loading');
            const packCopyResonse = await deletePackCopySpec(
                packCopyId,
                specName,
            );
            setPackCopyDetailed(packCopyResonse.pack_copy_detailed);
            setLoadingState('loaded');
        },
        handleSpecUpdate: async (specs: SpecDetail[]) => {
            setLoadingState('loading');
            const packCopyResonse = await putPackCopySpec(packCopyId, specs);
            setPackCopyDetailed(packCopyResonse.pack_copy_detailed);
            setLoadingState('loaded');
        },
        handleSpecCreation: async (specs: SpecDetail[]) => {
            setLoadingState('loading');
            const packCopyResonse = await postPackCopySpec(packCopyId, specs);
            setPackCopyDetailed(packCopyResonse.pack_copy_detailed);
            setLoadingState('loaded');
        },
        handlePackCopyNameUpdate: async (packCopyId: string, name: string) => {
            setPackCopyDetailed((prev) => {
                if (prev === null) {
                    return null; // Should not be reached due to component logic
                }
                return {
                    ...prev,
                    name: name,
                };
            });
            const packCopyResonse = await updatePackCopyName(packCopyId, name);
            setPackCopyDetailed(packCopyResonse.pack_copy_detailed);
            refresh(false);
        },
        handlePackCopyNotesUpdate: async (
            packCopyId: string,
            notes: string,
        ) => {
            setPackCopyDetailed((prev) => {
                if (prev === null) {
                    return null; // Should not be reached due to component logic
                }
                return {
                    ...prev,
                    notes: notes,
                };
            });
            const packCopyResonse = await updatePackCopyNotes(
                packCopyId,
                notes,
            );
            setPackCopyDetailed(packCopyResonse.pack_copy_detailed);
            refresh(false);
        },
    };

    return (
        <PackCopyContext.Provider value={{ state, handlers }}>
            {children}
        </PackCopyContext.Provider>
    );
};

export default PackCopyContextProvider;
